/**
 * AccessibilityMenu Component
 * Provides an accessibility drawer allowing users to customize font size,
 * high contrast modes, brightness overlays, and color blindness filters.
 * Preferences are saved in LocalStorage and applied to document elements.
 * Fully optimized for mobile/touch screens: stays open during interactions,
 * provides explicit close controls, mobile backdrop, and cross-instance sync.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './AccessibilityMenu.css';

const AccessibilityMenu = ({ isNavDrawerOpen = false, onOpen, className = '' }) => {
  // Accessibility toggle state variables
  const [isOpen, setIsOpen] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [largeText, setLargeText] = useState(false);
  const [brightnessLevel, setBrightnessLevel] = useState(100);
  const [chromoShade, setChromoShade] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();

  /**
   * Applies active accessibility class names to root document elements.
   * Applying filters to documentElement (html) avoids breaking position:fixed containing blocks.
   */
  const applyAccessibilitySettings = (contrast, textLarge, brightness, shade) => {
    document.body.classList.toggle('high-contrast', contrast);
    document.documentElement.classList.toggle('large-text', textLarge);
    const brightnessFilter = brightness === 100 ? '' : `brightness(${brightness / 100})`;
    const shadeFilter = shade ? 'grayscale(1) contrast(1.02)' : '';
    const filterValue = [brightnessFilter, shadeFilter].filter(Boolean).join(' ') || '';

    // Apply to html root so fixed-positioned children maintain standard viewport coords
    document.documentElement.style.filter = filterValue;
    // Clear filter on body in case legacy code set it
    document.body.style.filter = '';
  };

  // Broadcast settings change so all mounted AccessibilityMenu instances stay synchronized
  const broadcastSettings = (contrast, textLarge, brightness, shade) => {
    window.dispatchEvent(
      new CustomEvent('a11y-settings-sync', {
        detail: { contrast, textLarge, brightness, shade },
      })
    );
  };

  // Initialize from LocalStorage
  useEffect(() => {
    const savedContrast = localStorage.getItem('a11y-high-contrast') === 'true';
    const savedLargeText = localStorage.getItem('a11y-large-text') === 'true';
    const savedBrightnessLevel = localStorage.getItem('a11y-brightness-level');
    const savedBrightnessMode = localStorage.getItem('a11y-brightness-mode') || 'default';
    const savedChromoShade = localStorage.getItem('a11y-chromo-shade') === 'true';
    const migratedBrightness = savedBrightnessLevel
      ? Number.parseInt(savedBrightnessLevel, 10)
      : savedBrightnessMode === 'brighten'
        ? 108
        : savedBrightnessMode === 'darken'
          ? 90
          : 100;
    const normalizedBrightness = Number.isFinite(migratedBrightness) ? migratedBrightness : 100;

    setHighContrast(savedContrast);
    setLargeText(savedLargeText);
    setBrightnessLevel(normalizedBrightness);
    setChromoShade(savedChromoShade);
    localStorage.setItem('a11y-brightness-level', String(normalizedBrightness));
    applyAccessibilitySettings(savedContrast, savedLargeText, normalizedBrightness, savedChromoShade);
  }, []);

  // Listen for sync events from any other mounted AccessibilityMenu instance
  useEffect(() => {
    const handleSync = (e) => {
      if (!e.detail) return;
      const { contrast, textLarge, brightness, shade } = e.detail;
      if (typeof contrast === 'boolean') setHighContrast(contrast);
      if (typeof textLarge === 'boolean') setLargeText(textLarge);
      if (typeof brightness === 'number') setBrightnessLevel(brightness);
      if (typeof shade === 'boolean') setChromoShade(shade);
    };

    window.addEventListener('a11y-settings-sync', handleSync);
    return () => window.removeEventListener('a11y-settings-sync', handleSync);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close accessibility menu if the mobile nav drawer opens
  useEffect(() => {
    if (isNavDrawerOpen) {
      setIsOpen(false);
    }
  }, [isNavDrawerOpen]);

  // Handle outside interactions & escape key to close menu
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDownOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDownOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    const nextState = !isOpen;
    setIsOpen(nextState);
    if (nextState && onOpen) {
      onOpen();
    }
  };

  const closeMenu = (e) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
  };

  const toggleHighContrast = () => {
    const newVal = !highContrast;
    setHighContrast(newVal);
    localStorage.setItem('a11y-high-contrast', String(newVal));
    applyAccessibilitySettings(newVal, largeText, brightnessLevel, chromoShade);
    broadcastSettings(newVal, largeText, brightnessLevel, chromoShade);
  };

  const toggleLargeText = () => {
    const newVal = !largeText;
    setLargeText(newVal);
    localStorage.setItem('a11y-large-text', String(newVal));
    applyAccessibilitySettings(highContrast, newVal, brightnessLevel, chromoShade);
    broadcastSettings(highContrast, newVal, brightnessLevel, chromoShade);
  };

  const handleBrightnessChange = (event) => {
    const nextLevel = Number.parseInt(event.target.value, 10);
    const normalizedLevel = Number.isFinite(nextLevel) ? nextLevel : 100;
    setBrightnessLevel(normalizedLevel);
    localStorage.setItem('a11y-brightness-level', String(normalizedLevel));
    localStorage.removeItem('a11y-brightness-mode');
    applyAccessibilitySettings(highContrast, largeText, normalizedLevel, chromoShade);
    broadcastSettings(highContrast, largeText, normalizedLevel, chromoShade);
  };

  const toggleChromoShade = () => {
    const nextVal = !chromoShade;
    setChromoShade(nextVal);
    localStorage.setItem('a11y-chromo-shade', String(nextVal));
    applyAccessibilitySettings(highContrast, largeText, brightnessLevel, nextVal);
    broadcastSettings(highContrast, largeText, brightnessLevel, nextVal);
  };

  const resetAllSettings = () => {
    setHighContrast(false);
    setLargeText(false);
    setBrightnessLevel(100);
    setChromoShade(false);
    localStorage.removeItem('a11y-high-contrast');
    localStorage.removeItem('a11y-large-text');
    localStorage.removeItem('a11y-brightness-level');
    localStorage.removeItem('a11y-brightness-mode');
    localStorage.removeItem('a11y-chromo-shade');
    applyAccessibilitySettings(false, false, 100, false);
    broadcastSettings(false, false, 100, false);
  };

  return (
    <div className={`a11y-dropdown ${isOpen ? 'open' : ''} ${className}`} ref={menuRef}>
      {/* Mobile backdrop for easy dismissal and isolating page interactions */}
      {isOpen && (
        <div
          className="a11y-backdrop"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      <button
        type="button"
        className="a11y-toggle-btn"
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-label="Accessibility options"
        aria-haspopup="dialog"
      >
        <span className="a11y-icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="4"></circle>
            <line x1="4.93" y1="4.93" x2="9.17" y2="9.17"></line>
            <line x1="14.83" y1="14.83" x2="19.07" y2="19.07"></line>
            <line x1="14.83" y1="9.17" x2="19.07" y2="4.93"></line>
            <line x1="14.83" y1="9.17" x2="18.36" y2="5.64"></line>
            <line x1="4.93" y1="19.07" x2="9.17" y2="14.83"></line>
          </svg>
        </span>
      </button>

      {/* Accessibility Menu Drawer */}
      <div
        className={`a11y-menu ${isOpen ? 'show' : ''}`}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-label="Accessibility Settings"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="a11y-menu-header">
          <span className="a11y-header-title">Accessibility Settings</span>
          <button
            type="button"
            className="a11y-close-btn"
            onClick={closeMenu}
            aria-label="Close accessibility options"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="a11y-menu-item">
          <label className="a11y-toggle-label">
            <span className="a11y-toggle-text">High Contrast</span>
            <div className="a11y-switch-wrapper">
              <input
                type="checkbox"
                className="a11y-checkbox"
                checked={highContrast}
                onChange={toggleHighContrast}
                aria-label="Toggle high contrast mode"
              />
              <span className="a11y-toggle-switch"></span>
            </div>
          </label>
        </div>

        <div className="a11y-menu-item">
          <label className="a11y-toggle-label">
            <span className="a11y-toggle-text">Large Text</span>
            <div className="a11y-switch-wrapper">
              <input
                type="checkbox"
                className="a11y-checkbox"
                checked={largeText}
                onChange={toggleLargeText}
                aria-label="Toggle large text mode"
              />
              <span className="a11y-toggle-switch"></span>
            </div>
          </label>
        </div>

        <div className="a11y-menu-item">
          <div className="a11y-toggle-label a11y-toggle-label--stacked">
            <span className="a11y-toggle-text">Display Brightness</span>
            <div className="a11y-range-group">
              <div className="a11y-range-labels">
                <span>Darken (85%)</span>
                <span>Brighten (115%)</span>
              </div>
              <input
                type="range"
                min="85"
                max="115"
                step="1"
                value={brightnessLevel}
                onChange={handleBrightnessChange}
                aria-label="Adjust display brightness"
                className="a11y-range"
              />
              <div className="a11y-range-value">{brightnessLevel}%</div>
            </div>
          </div>
        </div>

        <div className="a11y-menu-item">
          <label className="a11y-toggle-label">
            <span className="a11y-toggle-text">Chromo Shade</span>
            <div className="a11y-switch-wrapper">
              <input
                type="checkbox"
                className="a11y-checkbox"
                checked={chromoShade}
                onChange={toggleChromoShade}
                aria-label="Toggle chromo shade mode"
              />
              <span className="a11y-toggle-switch"></span>
            </div>
          </label>
        </div>

        <div className="a11y-reset-wrapper">
          <button
            type="button"
            className="a11y-reset-btn"
            onClick={resetAllSettings}
          >
            Reset to Defaults
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityMenu;
